import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { ErrorState, Loading } from "../../components/States";
import { Dialog, PillView, SortTh, Toolbar, matchesSearch, useAction, useSorted } from "../../components/ui";
import { toast } from "../../components/Toast";
import { api } from "../../data";
import { money } from "../../lib/format";
import {
  useArchivedInvoices, useCreatorBase, useInvoiceDetails, type CBrand, type CDeal, type CInvoice, type CInvoiceEvent, type CPeriod, type OrgAddress, type PlatformDefaults,
} from "./data";
import {
  creatorRules, csvAmount, feeRest, invoiceTimeline, partialPaidOutCents, sameServiceDuplicates, talentActionLabel, talentActions, talentStateContext, toCsv, viewTalentInvoice,
  type TalentInvoiceView,
} from "./model";
import { NachprovisionHinweis } from "./MediaKit";
import { acceptSelfBilling, addLateReceipt, askAgencyAboutInvoice, askFormerAgencyAboutPayout, canAddLateReceipt, discardInvoice, loadAgencyContact, fetchDocument, objectSelfBilling, reportPayment, reportTransfer, retractPaymentReport, withdrawInvoice, withdrawSubmission } from "./actions";
import { agencyDocumentPdf } from "./rechnungPdf";
import type { CAttachment, CPayoutInfo, OrgAddress as FullOrg, TalentSelf } from "./data";
import { eInvoiceFormatLabel } from "@talentkontor/domain";
import { expectedOfInvoice } from "./eRechnung";
import { BELEG_ACCEPT, withStoredExtension } from "../../model/e-rechnung";
import { ConfirmDialog, DemoNote, downloadDocument, downloadText, openDocument, Timeline, useCreatorAction, useCreatorContext } from "./ui";
import { dateOf, monthOf, tr, useT, type Lang } from "./sprache";
import { zipFiles } from "./zip";

/** Deine Rechnungen (12_app_creator/rechnungsstatus.html) mit Liste, Filtern und Aktionen aus dem bisherigen Portal (TA-050 … TA-074). */

type Filter = "alle" | "action" | "open" | "done";
const BUCKET_RANK = { action: 0, open: 1, done: 2 } as const;

/** Verlauf aus Sicht des Creators (talent_invoice_events); unbekannte Schritte heißen „Schritt deiner Agentur“. */
const EVENT_TEXT: Record<string, [string, string]> = {
  created_draft: ["Entwurf angelegt", "Draft created"],
  created_submitted: ["Angelegt und eingereicht", "Created and submitted"],
  created_approved: ["Von deiner Agentur angelegt und freigegeben", "Created and approved by your agency"],
  created_sent: ["Von deiner Agentur angelegt und versendet", "Created and sent by your agency"],
  created_paid: ["Als bezahlt übernommen", "Imported as paid"],
  draft: ["Wieder in Bearbeitung", "Back in progress"],
  submitted: ["Eingereicht", "Submitted"],
  submission_withdrawn: ["Einreichung zurückgenommen", "Submission withdrawn"],
  approved: ["Von deiner Agentur freigegeben", "Approved by your agency"],
  approval_retracted: ["Freigabe zurückgezogen, Korrektur nötig", "Approval retracted, correction needed"],
  sent: ["Zur Auszahlung vorgemerkt (noch nicht überwiesen)", "Marked for payout (not yet transferred)"],
  paid: ["Bezahlt", "Paid"],
  revision_requested: ["Zur Korrektur zurückgegeben", "Returned for correction"],
  rejected: ["Abgelehnt", "Rejected"],
  rejection_reverted: ["Rückmeldung zurückgenommen, wieder in Prüfung", "Feedback withdrawn, back in review"],
  cancelled: ["Storniert", "Cancelled"],
  discarded: ["Verworfen", "Discarded"],
  withdrawn: ["Zurückgezogen", "Withdrawn"],
  payment_report_retracted: ["Zahlungsmeldung zurückgenommen", "Payment report retracted"],
  comment: ["Anmerkung", "Note"],
  payment_reported: ["Zahlung als angekommen gemeldet", "Payment reported as received"],
  payment_still_overdue: ["Gemeldet: Geld noch nicht angekommen", "Reported: money not yet received"],
  payment_check_requested: ["Deine Agentur fragt nach dem Zahlungseingang", "Your agency asks about the payment"],
  payout_confirmed: ["Auszahlung von deiner Agentur bestätigt", "Payout confirmed by your agency"],
  payout_revoked: ["Bestätigung der Auszahlung zurückgenommen", "Payout confirmation withdrawn"],
  payment_confirmed: ["Zahlung bestätigt", "Payment confirmed"],
  payment_recorded: ["Zahlungseingang gebucht", "Payment recorded"],
  payment_reset: ["Zahlung zurückgesetzt", "Payment reset"],
  payment_report_rejected: ["Deine Zahlungsmeldung wurde zurückgewiesen", "Your payment report was rejected"],
  correction_mail_resent: ["Korrektur-Hinweis erneut per Mail geschickt", "Correction note resent by email"],
  due_extended: ["Frist verlängert", "Due date extended"],
  collection: ["An Inkasso übergeben", "Handed over to debt collection"],
  collection_withdrawn: ["Vom Inkasso zurückgeholt", "Withdrawn from debt collection"],
  dunning_sent: ["Mahnung an die Marke verschickt", "Reminder sent to the brand"],
  dunning_recorded: ["Mahnung vermerkt", "Reminder recorded"],
  attachment_added: ["Beleg angehängt", "Document attached"],
  // Steuerprüfung L12 (0446)
  einvoice_received: ["E-Rechnung abgelegt (Original mit Prüfsumme)", "E-invoice stored (original with checksum)"],
  // E-1 (0448)
  einvoice_server_checked: ["E-Rechnung serverseitig geprüft", "E-invoice verified on the server"],
  einvoice_browser_mismatch: ["E-Rechnung serverseitig geprüft, die Werte des Servers gelten", "E-invoice verified on the server, the server values apply"],
  einvoice_server_check_failed: ["Serverseitige Prüfung der E-Rechnung fehlgeschlagen", "Server-side check of the e-invoice failed"],
  created_credit_note: ["Stornorechnung erstellt", "Credit note created"],
  self_billing_objected: ["Du hast der Gutschrift widersprochen", "You objected to the self-billed invoice"],
  // Schritte aus der Nachprüfung der Rechnungen (0055)
  pdf_archived: ["Beleg unveränderbar abgelegt", "Document stored unalterably"],
  approved_with_findings: ["Von deiner Agentur trotz Prüfhinweisen freigegeben", "Approved by your agency despite review notes"],
  payout_queued: ["Zur Auszahlung vorgemerkt", "Queued for payout"],
  payout_advance: ["Vor Zahlung der Marke zur Auszahlung gegeben", "Paid out before the brand paid"],
  // 0083: anteilige Auszahlung, weil die Marke erst teilweise gezahlt hat (GELD-22)
  payout_partial: ["Anteilig zur Auszahlung gegeben (die Marke hat erst teilweise gezahlt)", "Partially queued for payout (the brand has only paid in part)"],
  payout_partial_paid: ["Anteil ausgezahlt, der Rest folgt nach Zahlung der Marke", "Share paid out, the rest follows once the brand pays"],
  payout_auto_confirmed: ["Als ausgezahlt übernommen (kein Widerspruch innerhalb der Frist)", "Treated as paid out (no objection within the period)"],
  offset: ["Mit einer Forderung deiner Agentur verrechnet", "Offset against a claim of your agency"],
  settled_via_brand_payment: ["Erledigt: Die Marke hat direkt an dich gezahlt", "Settled: the brand paid you directly"],
  sent_to_talent: ["Rechnung an dich verschickt", "Invoice sent to you"],
  resent_to_talent: ["Rechnung erneut an dich verschickt", "Invoice sent to you again"],
  // K-160: übrige Schritte mit Klartext statt „Schritt deiner Agentur“
  sent_to_brand: ["An die Marke gesendet", "Sent to the brand"],
  resent_to_brand: ["Erneut an die Marke gesendet", "Sent to the brand again"],
  delivery_recorded: ["Zugang bei der Marke vermerkt", "Receipt by the brand noted"],
  dunning_blocked: ["Mahnsperre gesetzt", "Dunning block set"],
  dunning_unblocked: ["Mahnsperre aufgehoben", "Dunning block lifted"],
  dunning_reset: ["Mahnstufe zurückgesetzt", "Dunning level reset"],
  bad_debt: ["Als uneinbringlich ausgebucht", "Written off as uncollectible"],
  cost_note_created: ["Beleg über Verzugskosten erstellt", "Late-payment cost note created"],
  overpayment_settled: ["Mehrbetrag als erledigt vermerkt", "Overpayment marked as settled"],
  imported: ["Aus dem bisherigen System übernommen", "Taken over from the previous system"],
  self_billing_accepted: ["Du hast die Gutschrift angenommen", "You accepted the self-billing invoice"],
};
const eventText = (e: string, lang: Lang) => {
  const x = EVENT_TEXT[e];
  return x ? tr(lang, x[0], x[1]) : tr(lang, "Schritt deiner Agentur", "Step by your agency");
};

type AskKind = "withdraw" | "discard" | "pay" | "withdrawSubmission" | "retract" | "ask" | "object" | "correction" | "accept";
type Pending = { kind: AskKind; v: TalentInvoiceView; correction?: boolean } | null;

interface Source {
  org: Pick<OrgAddress, "name" | "currency" | "timezone"> & Partial<OrgAddress>;
  defaults: PlatformDefaults;
  invoices: CInvoice[];
  agencyInvoices: CInvoice[];
  deals: CDeal[];
  periods: CPeriod[];
  brands: Map<string, CBrand>;
  /** Auszahlung je Rechnung an die Agentur (0055); im Archiv nicht geladen */
  payoutInfo?: Map<string, CPayoutInfo>;
  talent?: TalentSelf;
}

/** Beleg im Portal geschrieben (Gutschrift, Rechnung der Agentur an dich): archiviertes PDF bzw. aus den eingefrorenen Angaben */
const portalDocument = (i: CInvoice) => !!i.self_billed || i.kind === "agency_to_talent";

export function Rechnungen() {
  const { ws, scope, today, lang } = useCreatorContext();
  const archived = !!ws.archived;
  const base = useCreatorBase(scope, !archived);
  const old = useArchivedInvoices(scope, archived);
  const q = archived ? old : base;
  const src: Source | null = archived
    ? old.data ? { org: ws.org, defaults: old.data.defaults, invoices: old.data.invoices, agencyInvoices: old.data.agencyInvoices, deals: [], periods: [], brands: new Map(), ...(old.data.talent ? { talent: old.data.talent } : {}) } : null
    : base.data ? base.data : null;
  return q.error ? <ErrorState error={q.error} retry={() => void q.refetch()} />
    : !src ? <Loading />
    : <InvoiceList src={src} archived={archived} refetch={() => q.refetch()} fetching={q.isFetching} today={today} lang={lang} />;
}

function InvoiceList({ src: b, archived, refetch, fetching, today, lang }: { src: Source; archived: boolean; refetch: () => Promise<unknown>; fetching: boolean; today: string; lang: Lang }) {
  const { scope } = useCreatorContext();
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("alle");
  const [brandFilter, setBrandFilter] = useState("");
  const [pending, setPending] = useState<Pending>(null);
  const [message, setMessage] = useState("");
  const [zip, setZip] = useState<{ open: boolean; year: string; month: string }>({ open: false, year: today.slice(0, 4), month: "" });
  const { run, busy } = useAction();
  const fmt = (d: string | null | undefined) => dateOf(d ?? null, lang, b.org.timezone);
  // CREATOR-6: im Archiv Name und Antwortadresse der früheren Agentur (Chat und Nachrichten sind dort geschlossen)
  const contact = useQuery({ queryKey: ["creator", "agency-contact", scope.orgId], enabled: archived, queryFn: () => loadAgencyContact(api, scope.orgId) });

  const data = useMemo(() => {
    const rules = creatorRules(b.defaults, b.org);
    const ctx = talentStateContext(today, rules, b.org.currency);
    // Fälligkeit der Auszahlung nach dem Modus der Agentur (im Modus „nach Zahlungseingang“ ab Zahlung der Marke, Befund Creator 6)
    const payoutDue = b.payoutInfo ? new Map([...b.payoutInfo.values()].map((p) => [p.invoice_id, p.payout_due ?? null])) : undefined;
    const views = b.invoices.map((r) => viewTalentInvoice(r, ctx, b.org.timezone, lang, { ...rules, ...(payoutDue ? { payoutDue } : {}) })).filter((v): v is TalentInvoiceView => !!v);
    const deals = new Map(b.deals.map((d) => [d.id, d]));
    const periods = new Map(b.periods.map((p) => [p.id, p]));
    const dup = sameServiceDuplicates(views);
    const rows = views.map((v) => {
      const d = v.row.deal_id ? deals.get(v.row.deal_id) : undefined;
      // Im Archiv sind Kooperationen nicht mehr lesbar: die Gegenseite nennt der eingefrorene Empfänger
      const brand = d ? b.brands.get(d.brand_id)?.name ?? "" : v.row.recipient_snapshot?.name ?? "";
      const payout = v.row.kind === "talent_to_agency" && v.record.payoutDueDate !== undefined;
      return { v, deal: d, brand, due: payout ? v.record.payoutDueDate ?? null : v.row.effective_due_date ?? v.row.document_due_date ?? v.row.issuer_due_date, afterBrand: payout && !v.record.payoutDueDate };
    });
    rows.sort((x, y) => BUCKET_RANK[x.v.bucket] - BUCKET_RANK[y.v.bucket]
      || (x.v.bucket === "done" ? (y.v.row.issue_date ?? "").localeCompare(x.v.row.issue_date ?? "") : (x.due ?? "9999").localeCompare(y.due ?? "9999"))
      || (x.v.row.number ?? "").localeCompare(y.v.row.number ?? ""));
    return { ctx, rows, periods, dup, deals };
  }, [b, today, lang]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { alle: 0, action: 0, open: 0, done: 0 };
    for (const r of data.rows) {
      c[r.v.bucket] += 1;
      if (r.v.bucket !== "done") c.alle += 1;
    }
    return c;
  }, [data]);

  // RE-38: „Alle“ ohne Erledigte; eine Suche findet auch Erledigtes
  const shown = data.rows.filter((r) => {
    const inFilter = search.trim() ? true : filter === "alle" ? r.v.bucket !== "done" : r.v.bucket === filter;
    const text = `${r.v.row.number ?? ""} ${r.brand} ${r.deal?.title ?? ""} ${r.v.pill.label} ${r.v.row.revenue_month ? monthOf(r.v.row.revenue_month, lang) : ""}`;
    return inFilter && (!brandFilter || r.deal?.brand_id === brandFilter) && matchesSearch(text, search);
  });
  const { sorted, sort, toggle } = useSorted(shown, {
    nummer: (r) => r.v.row.issue_date,
    kooperation: (r) => `${r.brand} ${r.deal?.title ?? ""}`,
    status: (r) => r.v.pill.label,
    faellig: (r) => r.due,
    betrag: (r) => r.v.row.gross_cents,
  });

  const selectedId = params.get("rechnung") ?? sorted.find((r) => r.v.bucket !== "done")?.v.row.id ?? null;
  const selected = data.rows.find((r) => r.v.row.id === selectedId) ?? null;
  const details = useInvoiceDetails(scope, selected ? [selected.v.row.id] : []);

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("rechnung", id);
    setParams(next, { replace: true });
  };
  const corrections = data.rows.filter((r) => r.v.state.state === "correction_needed");
  const overdue = data.rows.filter((r) => (r.v.state.isOverdue || r.v.state.state === "send_overdue" || r.v.state.state === "payout_overdue") && r.v.bucket !== "done");
  const brandsWithInvoices = [...new Set(data.rows.map((r) => r.deal?.brand_id).filter((x): x is string => !!x))];
  const hasDirect = b.invoices.some((i) => i.kind === "talent_to_brand") || b.deals.some((d) => d.invoice_route === "direct_to_brand");
  const years = [...new Set([today.slice(0, 4), ...b.invoices.map((i) => (i.issue_date ?? "").slice(0, 4)).filter(Boolean)])].sort().reverse();
  // Auch Gutschriften und Rechnungen der Agentur an dich (Befund 5): archiviertes PDF bzw. aus den eingefrorenen Angaben
  const zipRows = [...data.rows.map((r) => r.v.row), ...b.agencyInvoices]
    .filter((i) => (i.pdf_path || (portalDocument(i) && !!i.number)) && !["draft", "discarded", "withdrawn"].includes(i.status)
      && (i.issue_date ?? "").startsWith(zip.month ? `${zip.year}-${zip.month}` : zip.year));
  const openPortalDocument = (i: CInvoice) => void run(`pdf:${i.id}`, async () => {
    if (!b.talent && i.pdf_path) return void openDocument(i.pdf_path, `${i.number ?? "rechnung"}.pdf`);
    if (!b.talent) throw new Error(t("Das PDF lässt sich gerade nicht erzeugen.", "The PDF cannot be created right now."));
    const f = await agencyDocumentPdf(api, i, b.org as FullOrg, b.talent);
    const url = URL.createObjectURL(f.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.fileName;
    a.target = "_blank";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  });

  function exportCsv() {
    const lines = [
      [t("Rechnung", "Invoice"), t("Datum", "Date"), t("Marke", "Brand"), t("Kooperation", "Collaboration"), t("Leistungsmonat", "Service month"), "Status",
        t("Fällig", "Due"), t("Währung", "Currency"), t("Netto", "Net"), t("Umsatzsteuer", "VAT"), t("Brutto", "Gross")],
      ...sorted.map((r) => [
        r.v.row.number ?? "", r.v.row.issue_date ?? "", r.brand, r.deal?.title ?? "", r.v.row.revenue_month ? monthOf(r.v.row.revenue_month, lang) : "",
        r.v.pill.label, r.due ?? "", r.v.row.currency, csvAmount(r.v.row.net_cents), csvAmount(r.v.row.tax_cents), csvAmount(r.v.row.gross_cents),
      ]),
    ];
    downloadText(`${t("Rechnungen", "Invoices")}_${today}.csv`, toCsv(lines));
  }

  // Befund 23: alle PDFs eines Jahres oder Monats gesammelt, z. B. für die Steuererklärung
  async function downloadZip() {
    const files = await run("zip", async () => {
      const out: { name: string; data: Uint8Array }[] = [];
      for (const i of zipRows) {
        const doc = portalDocument(i) && b.talent ? await agencyDocumentPdf(api, i, b.org as FullOrg, b.talent) : null;
        const blob = doc ? doc.blob : await fetchDocument(api, i.pdf_path!);
        const name = doc ? doc.fileName : withStoredExtension(i.pdf_path!.split("/").pop()?.replace(/^\d+-/, "") ?? "rechnung.pdf", i.pdf_path);
        out.push({ name: `${i.issue_date ?? ""}_${i.number ?? "ohne-Nummer"}_${name}`.replace(/\s+/g, "-"), data: new Uint8Array(await blob.arrayBuffer()) });
      }
      return out;
    });
    if (!files?.length) return;
    const bytes = zipFiles(files);
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/zip" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t("Rechnungen", "Invoices")}_${zip.year}${zip.month ? `-${zip.month}` : ""}.zip`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    setZip((z) => ({ ...z, open: false }));
    toast(t(`${files.length} Rechnungen als ZIP gespeichert.`, `${files.length} invoices saved as ZIP.`));
  }

  async function confirmPending(response?: "paid" | "still_overdue") {
    if (!pending) return;
    const id = pending.v.row.id;
    let ok: unknown;
    switch (pending.kind) {
      case "withdraw":
        ok = await run("withdraw", () => withdrawInvoice(api, scope, id), t("Rechnung zurückgezogen. Die Nummer ist wieder frei.", "Invoice withdrawn. The number is free again."));
        break;
      case "discard":
        ok = await run("discard", () => discardInvoice(api, scope, id), t("Entwurf verworfen. Seine Nummer ist wieder frei.", "Draft discarded. Its number is free again."));
        break;
      case "withdrawSubmission":
        ok = await run("withdrawSubmission", () => withdrawSubmission(api, id), t("Einreichung zurückgenommen. Die Rechnung ist wieder ein Entwurf.", "Submission withdrawn. The invoice is a draft again."));
        break;
      case "retract":
        ok = await run("retract", () => retractPaymentReport(api, id), t("Deine Zahlungsmeldung ist zurückgenommen.", "Your payment report has been retracted."));
        break;
      case "ask":
        // Archiv (CREATOR-6): Chat geschlossen, die Nachfrage geht als Aufgabe mit Mail an das Team der früheren Agentur
        ok = archived
          ? await run("ask", () => askFormerAgencyAboutPayout(api, id, message), t("Deine Nachfrage ist bei deiner früheren Agentur. Die Antwort kommt per Mail.", "Your question has reached your former agency. The answer will come by email."))
          : await run("ask", () => askAgencyAboutInvoice(api, scope.talentId, id, message), t("Deine Nachricht ist bei deiner Agentur.", "Your message has been sent to your agency."));
        if (ok !== undefined) setMessage("");
        break;
      case "object":
        ok = await run("object", () => objectSelfBilling(api, id, message), t("Dein Widerspruch ist bei deiner Agentur. Die Gutschrift ist storniert, den Stornobeleg bekommst du per Mail. Deine Agentur stellt die Gutschrift neu aus oder meldet sich bei dir.", "Your objection has reached your agency. The self-billed invoice is cancelled; you will receive the cancellation document by email. Your agency will reissue the self-billed invoice or get in touch with you."));
        if (ok !== undefined) setMessage("");
        break;
      case "pay":
        ok = await run("pay", () => reportPayment(api, id, response ?? "paid"), response === "still_overdue"
          ? t("Danke, deine Agentur sieht: Das Geld fehlt noch.", "Thanks, your agency can see that the money is still missing.")
          : t("Danke, deine Zahlungsmeldung ist bei deiner Agentur. Falls sie falsch war, kannst du sie hier zurücknehmen.", "Thanks, your agency has your payment report. If it was a mistake, you can retract it here."));
        break;
    }
    if (ok !== undefined) setPending(null);
  }

  const openAsk = (v: TalentInvoiceView) => {
    setMessage(tr(lang,
      `Hallo, zur Rechnung ${v.row.number ?? ""}: Die Auszahlung war am ${fmt(v.row.effective_due_date ?? v.row.issuer_due_date)} fällig und ist noch nicht bei mir angekommen. Wann kann ich damit rechnen?`,
      `Hi, about invoice ${v.row.number ?? ""}: the payout was due on ${fmt(v.row.effective_due_date ?? v.row.issuer_due_date)} and has not reached me yet. When can I expect it?`));
    setPending({ kind: "ask", v });
  };

  const actionButton = (r: (typeof sorted)[number]) => {
    const a = r.v.primary;
    const label = talentActionLabel(a, lang);
    const key = a?.action;
    const acts = talentActions(r.v, data.ctx);
    if (!archived && (key === "edit" || key === "correct" || key === "submit")) {
      return <Link className="btn" to={`/creator/rechnungen/einreichen?rechnung=${r.v.row.id}${key === "submit" ? "&schritt=4" : ""}`}>{label}</Link>;
    }
    if (key === "report_paid") return <button onClick={() => setPending({ kind: "pay", v: r.v })}>{label}</button>;
    if (key === "withdraw_submission" && acts.withdrawSubmission && !archived) return <button onClick={() => setPending({ kind: "withdrawSubmission", v: r.v })}>{label}</button>;
    // Im Archiv (CREATOR-6) nur für offene Auszahlungen: dafür gibt es dort die Nachfrage per Aufgabe
    if (acts.askAgency && (!archived || (r.v.row.kind === "talent_to_agency" && ["approved", "sent"].includes(r.v.row.status)))) return <button onClick={() => openAsk(r.v)}>{t("Bei der Agentur nachfragen", "Ask your agency")}</button>;
    if (key === "open_pdf" && portalDocument(r.v.row)) return <button onClick={() => openPortalDocument(r.v.row)}>{label}</button>;
    if (key === "open_pdf" && r.v.row.pdf_path) return <button onClick={() => void openDocument(r.v.row.pdf_path!)}>{label}</button>;
    // Hinweis statt Aktion (z. B. „Anteilig ausgezahlt · Rest folgt“, GELD-R3-1): der Knopf öffnet die Details
    if (a?.inert) return <button onClick={() => select(r.v.row.id)}>{label}</button>;
    return <button onClick={() => select(r.v.row.id)}>{t("Details ansehen", "View details")}</button>;
  };

  const filters: { key: Filter; label: string }[] = [
    { key: "alle", label: t("Alle", "All") },
    { key: "action", label: t("Handlungsbedarf", "Action needed") },
    { key: "open", label: t("Offen", "Open") },
    { key: "done", label: t("Erledigt", "Done") },
  ];

  return (
    <main id="main" className="content tk-cr tk-cr-rechnungen">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{t("Deine Rechnungen", "Your invoices")}</h1>
          <p>
            {archived
              ? t(`Rechnungen bei ${b.org.name} (archiviert): lesen, PDFs sichern und offene Auszahlungen melden.`, `Invoices with ${b.org.name} (archived): read them, save the PDFs and report open payouts.`)
              : hasDirect
                ? t(`Rechnungen an ${b.org.name} und, bei direkter Abrechnung, an Marken: Prüfung, Freigabe und Zahlung auf einen Blick.`, `Invoices to ${b.org.name} and, where you bill directly, to brands: review, approval and payment at a glance.`)
                : t(`Rechnungen an ${b.org.name}: Prüfung, Freigabe und Auszahlung auf einen Blick.`, `Invoices to ${b.org.name}: review, approval and payout at a glance.`)}
          </p>
        </div>
        <div className="row">
          <button onClick={() => void refetch().then(() => toast(t("Rechnungen aktualisiert.", "Invoices refreshed.")))} disabled={fetching}>{t("Aktualisieren", "Refresh")}</button>
          {!archived && <Link className="btn primary" to="/creator/rechnungen/einreichen" data-coco="rechnung-einreichen">{t("Rechnung einreichen", "Submit invoice")}</Link>}
        </div>
      </div>

      <NachprovisionHinweis talentId={scope.talentId} />
      {archived && contact.data && (contact.data.email || contact.data.phone) && (
        <p className="muted">{t(`Kontakt zu ${contact.data.name ?? b.org.name}: `, `Contact ${contact.data.name ?? b.org.name}: `)}
          {contact.data.email && <a href={`mailto:${contact.data.email}`}>{contact.data.email}</a>}{contact.data.email && contact.data.phone ? " · " : ""}{contact.data.phone}</p>
      )}

      {(counts.action > 0 || overdue.length > 0) && (
        <div className="integrity-banner">
          {counts.action > 0 ? (
            <p>
              <strong>{counts.action === 1 ? t("1 Rechnung braucht dich", "1 invoice needs you") : t(`${counts.action} Rechnungen brauchen dich`, `${counts.action} invoices need you`)}</strong>
              {" · "}{t("Entwurf, Korrektur, Gutschrift zum Prüfen oder Zahlungsmeldung", "draft, correction, self-billed invoice to check or payment report")}{overdue.length > 0 ? t(` · ${overdue.length} überfällig`, ` · ${overdue.length} overdue`) : ""}.
            </p>
          ) : (
            <p>
              <strong>{overdue.length === 1 ? t("1 Rechnung ist überfällig", "1 invoice is overdue") : t(`${overdue.length} Rechnungen sind überfällig`, `${overdue.length} invoices are overdue`)}</strong>
              {" · "}{t("bei Auszahlung oder Zahlung; du kannst bei deiner Agentur nachfragen.", "in payout or payment; you can ask your agency.")}
            </p>
          )}
          <button className="btn" onClick={() => setFilter(counts.action > 0 ? "action" : "open")}>{t("Anzeigen", "Show")}</button>
        </div>
      )}

      {selected && (
        <InvoicePanel
          key={selected.v.row.id}
          r={selected}
          ctx={data.ctx}
          archived={archived}
          lang={lang}
          timeZone={b.org.timezone}
          period={selected.v.row.period_id ? data.periods.get(selected.v.row.period_id) : undefined}
          details={details.data}
          duplicates={(data.dup.get(selected.v.row.id) ?? []).map((id) => data.rows.find((x) => x.v.row.id === id)?.v.row.number ?? t("ohne Nummer", "no number"))}
          stornoNumber={data.rows.find((x) => x.v.row.credit_for === selected.v.row.id && x.v.row.self_billed)?.v.row.number ?? null}
          onAsk={(kind) => {
            if (kind === "ask") return openAsk(selected.v);
            if (kind === "correction") {
              // Korrektur nach der Freigabe erbitten: Nachricht an die Agentur mit Verweis auf die Rechnung (Befund Creator 13)
              setMessage(tr(lang, `Hallo, zur Rechnung ${selected.v.row.number ?? ""}: Ich muss daran etwas korrigieren: `, `Hi, about invoice ${selected.v.row.number ?? ""}: I need to correct something: `));
              return setPending({ kind: "ask", v: selected.v, correction: true });
            }
            if (kind === "object") setMessage("");
            // Gutschrift „Passt so“ (CREATOR-R3-5): ohne Rückfrage, der Widerspruch bleibt möglich
            if (kind === "accept") {
              void run("accept", () => acceptSelfBilling(api, selected.v.row.id), t("Danke, die Gutschrift ist als geprüft vermerkt. Widersprechen kannst du bis zur Auszahlung.", "Thanks, the self-billed invoice is marked as checked. You can still object until the payout."));
              return;
            }
            setPending({ kind, v: selected.v });
          }}
          payoutInfo={b.payoutInfo?.get(selected.v.row.id)}
          onPdf={portalDocument(selected.v.row) ? () => openPortalDocument(selected.v.row) : undefined}
        />
      )}

      {!archived && corrections.filter((r) => r.v.row.id !== selected?.v.row.id).map((r) => (
        <section key={r.v.row.id} className="panel stack">
          <h3>{t("Korrektur erforderlich", "Correction required")}</h3>
          <span className="pill error"><span className="pill-zeichen">!</span> {t("Rechnung", "Invoice")} {r.v.row.number ?? t("ohne Nummer", "no number")}</span>
          <p>{r.v.row.revision_reason?.trim() || t("Deine Agentur hat die Rechnung zur Korrektur zurückgegeben.", "Your agency has returned the invoice for correction.")} {t("Dein bisheriger Beleg bleibt im Verlauf erhalten.", "Your previous document stays in the history.")}</p>
          <Link className="btn " to={`/creator/rechnungen/einreichen?rechnung=${r.v.row.id}`}>{t("Rechnung korrigieren", "Correct invoice")}</Link>
        </section>
      ))}

      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Rechnungen suchen", "Search invoices")}
        placeholder={t("Nummer, Marke oder Monat …", "Number, brand or month …")}
        filters={filters.map((f) => ({ ...f, count: f.key === "done" ? undefined : counts[f.key] }))}
        filter={filter}
        onFilter={setFilter}
      >
        {brandsWithInvoices.length > 1 && (
          <select aria-label={t("Nach Marke filtern", "Filter by brand")} value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
            <option value="">{t("Alle Marken", "All brands")}</option>
            {brandsWithInvoices.map((id) => <option key={id} value={id}>{b.brands.get(id)?.name ?? t("Marke", "Brand")}</option>)}
          </select>
        )}
        <button onClick={exportCsv} disabled={sorted.length === 0}>{t("Als CSV exportieren", "Export as CSV")}</button>
        <button onClick={() => setZip((z) => ({ ...z, open: true }))}
          disabled={![...data.rows.map((r) => r.v.row), ...b.agencyInvoices].some((i) => i.pdf_path || (portalDocument(i) && !!i.number))}>{t("PDFs als ZIP", "PDFs as ZIP")}</button>
      </Toolbar>

      <div className="tablewrap tk-cardtable tk-cardwide tk-cr-liste">
        <table>
          <caption className="sr-only">{t("Deine Rechnungen", "Your invoices")}</caption>
          <thead>
            <tr>
              <SortTh label={t("Rechnung / Datum", "Invoice / date")} k="nummer" sort={sort} toggle={toggle} />
              <SortTh label={t("Kooperation / Monat", "Collaboration / month")} k="kooperation" sort={sort} toggle={toggle} />
              <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
              <SortTh label={t("Fällig", "Due")} k="faellig" sort={sort} toggle={toggle} />
              <SortTh label={t("Betrag brutto", "Gross amount")} k="betrag" sort={sort} toggle={toggle} money />
              <th scope="col">{t("Nächster Schritt", "Next step")}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.v.row.id} className={r.v.row.id === selected?.v.row.id ? "tk-selected" : undefined}>
                <td data-label={t("Rechnung", "Invoice")}>
                  <strong><Link to={`/creator/rechnungen?rechnung=${r.v.row.id}`} replace>{r.v.row.number ?? t("Ohne Nummer", "No number")}</Link></strong>
                  <small>{fmt(r.v.row.issue_date)}{r.v.row.self_billed ? (r.v.row.credit_for ? t(" · Stornobeleg zur Gutschrift", " · cancellation of a self-billed invoice") : t(" · Gutschrift deiner Agentur", " · self-billed by your agency")) : ""}</small>
                </td>
                <td data-label={t("Kooperation", "Collaboration")}>
                  {r.brand ? `${r.brand}${r.deal ? ` · ${r.deal.title}` : ""}` : r.deal?.title ?? "–"}
                  <small>
                    {r.v.row.revenue_month ? monthOf(r.v.row.revenue_month, lang) : ""}
                    {!archived && !r.v.row.period_id && r.v.row.deal_id ? t(" · ohne Monat", " · no month") : ""}
                    {data.dup.has(r.v.row.id) ? t(" · Achtung: weitere Rechnung für denselben Monat", " · Note: another invoice for the same month") : ""}
                  </small>
                </td>
                <td data-label="Status"><PillView pill={r.v.pill} /></td>
                <td data-label={t("Fällig", "Due")}>{r.afterBrand ? t("nach Zahlung der Marke", "after the brand pays") : fmt(r.due)}</td>
                <td data-label={t("Brutto", "Gross")}><span className="num">{money(r.v.row.gross_cents, r.v.row.currency)}</span></td>
                <td className="tk-cardaction">{actionButton(r)}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={6}>{search || brandFilter ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or filters.")
                  : filter === "alle" ? t("Gerade ist nichts offen. Neue Rechnungen reichst du über „Rechnung einreichen“ ein.", "Nothing is open right now. Submit new invoices via “Submit invoice”.")
                  : t("In dieser Ansicht gibt es keine Rechnungen.", "There are no invoices in this view.")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="source-note">
        {sorted.length === 1 ? t("1 Rechnung", "1 invoice") : t(`${sorted.length} Rechnungen`, `${sorted.length} invoices`)} · {t("„Alle“ zeigt alles außer Erledigtem; die Suche findet auch erledigte Rechnungen. Mahnschreiben an Marken siehst du hier nicht.",
          "“All” shows everything except completed invoices; the search also finds completed ones. Reminders sent to brands are not shown here.")}
      </p>

      {b.agencyInvoices.length > 0 && (
        <section className="panel stack" aria-labelledby="agentur-rechnungen">
          <h2 id="agentur-rechnungen">{t("Rechnungen deiner Agentur an dich", "Invoices from your agency to you")}</h2>
          <p>{t("Bei Kooperationen, die du direkt mit der Marke abrechnest, stellt dir deine Agentur ihre Provision in Rechnung.", "For collaborations you bill directly to the brand, your agency invoices you for its commission.")}</p>
          <div className="tablewrap tk-cardtable tk-cardwide tk-cr-liste">
            <table>
              <caption className="sr-only">{t("Rechnungen deiner Agentur an dich", "Invoices from your agency to you")}</caption>
              <thead><tr><th scope="col">{t("Nummer", "Number")}</th><th scope="col">{t("Kooperation", "Collaboration")}</th><th scope="col">{t("Fällig", "Due")}</th><th scope="col" className="money">{t("Betrag", "Amount")}</th><th scope="col">{t("Stand", "Status")}</th><th scope="col"><span className="sr-only">PDF</span></th></tr></thead>
              <tbody>
                {b.agencyInvoices.map((i) => {
                  const d = i.deal_id ? data.deals.get(i.deal_id) : undefined;
                  const due = i.effective_due_date ?? i.document_due_date;
                  const late = i.status === "sent" && !!due && due < today;
                  return (
                    <tr key={i.id}>
                      <td data-label={t("Nummer", "Number")}>{i.number ?? "–"}<small>{i.issue_date ? fmt(i.issue_date) : ""}</small></td>
                      <td data-label={t("Kooperation", "Collaboration")}>{d ? `${b.brands.get(d.brand_id)?.name ?? ""} · ${d.title}` : "–"}{i.revenue_month ? <small>{monthOf(i.revenue_month, lang)}</small> : null}</td>
                      <td data-label={t("Fällig", "Due")}>{due ? fmt(due) : "–"}</td>
                      <td data-label={t("Betrag", "Amount")} className="num">{money(i.gross_cents, i.currency)}
                        {i.status === "sent" && feeRest(i) !== i.gross_cents && <small>{t(`offen ${money(feeRest(i), i.currency)}`, `open ${money(feeRest(i), i.currency)}`)}</small>}</td>
                      <td data-label={t("Stand", "Status")}>{i.status === "paid" ? <span className="pill success"><span className="pill-zeichen">✓</span> {t("Bezahlt", "Paid")}</span> : i.status === "cancelled" ? <span className="pill"><span className="pill-zeichen">○</span> {t("Storniert", "Cancelled")}</span>
                        : i.talent_payment_response === "paid" ? <span className="pill info"><span className="pill-zeichen">◇</span> {t(`Überwiesen gemeldet am ${fmt(i.talent_payment_response_at)} · Agentur bestätigt`, `Transfer reported on ${fmt(i.talent_payment_response_at)} · agency confirms`)}</span>
                        : late ? <span className="pill error"><span className="pill-zeichen">!</span> {t("Überfällig", "Overdue")}</span> : <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Offen", "Open")}</span>}</td>
                      <td className="tk-cardaction">
                        {i.number ? <button disabled={busy === `pdf:${i.id}`} onClick={() => openPortalDocument(i)}>{t("PDF öffnen", "Open PDF")}</button> : null}
                        {/* CREATOR-R3-6: eigene Überweisung melden; die Agentur erfasst den Eingang */}
                        {!archived && canReportTransfer(i) && <button disabled={busy === `transfer:${i.id}`} onClick={() => void run(`transfer:${i.id}`, () => reportTransfer(api, i.id),
                          t("Danke, deine Agentur sieht deine Überweisung und bestätigt den Eingang.", "Thanks, your agency sees your transfer and confirms receipt."))}>{t("Ich habe überwiesen", "I have transferred")}</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <small>{t("Hast du überwiesen, melde es mit „Ich habe überwiesen“; deine Agentur bestätigt den Eingang, sobald er auf ihrem Konto ist. Fragen dazu klärst du am schnellsten über „Nachrichten“.", "Once you have transferred, report it with “I have transferred”; your agency confirms receipt once it is in its account. The quickest way to ask about it is via “Messages”.")}</small>
        </section>
      )}

      <ConfirmDialog
        open={pending?.kind === "withdraw"}
        title={t("Rechnung zurückziehen?", "Withdraw invoice?")}
        text={t("Die Rechnung wird nie wirksam und ihre Nummer ist wieder frei. Danach kannst du eine neue Rechnung für diesen Monat einreichen.", "The invoice never takes effect and its number is free again. You can then submit a new invoice for this month.")}
        confirm={t("Zurückziehen", "Withdraw")}
        danger
        busy={busy === "withdraw"}
        onConfirm={() => void confirmPending()}
        onClose={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.kind === "discard"}
        title={t("Entwurf verwerfen?", "Discard draft?")}
        text={t("Der Entwurf bleibt im Verlauf, zählt aber nicht mehr. Seine Nummer ist wieder frei.", "The draft stays in the history but no longer counts. Its number is free again.")}
        confirm={t("Verwerfen", "Discard")}
        danger
        busy={busy === "discard"}
        onConfirm={() => void confirmPending()}
        onClose={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.kind === "withdrawSubmission"}
        title={t("Einreichung zurückziehen?", "Withdraw submission?")}
        text={t("Deine Agentur hat die Rechnung noch nicht geprüft. Sie wird wieder ein Entwurf, den du ändern und erneut einreichen kannst. Die Nummer bleibt für diesen Entwurf reserviert.",
          "Your agency has not reviewed the invoice yet. It becomes a draft again that you can change and resubmit. The number stays reserved for this draft.")}
        confirm={t("Zurückziehen", "Withdraw")}
        busy={busy === "withdrawSubmission"}
        onConfirm={() => void confirmPending()}
        onClose={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.kind === "retract"}
        title={t("Zahlungsmeldung zurücknehmen?", "Retract payment report?")}
        text={t("Deine Meldung wird gelöscht; deine Agentur sieht wieder, dass die Zahlung offen ist. Das geht, solange sie die Zahlung nicht gebucht hat.",
          "Your report will be removed; your agency will see the payment as open again. This is possible as long as it has not booked the payment.")}
        confirm={t("Meldung zurücknehmen", "Retract report")}
        busy={busy === "retract"}
        onConfirm={() => void confirmPending()}
        onClose={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending?.kind === "ask"}
        title={pending?.correction ? t("Korrektur anfragen", "Request a correction") : t("Bei deiner Agentur nachfragen", "Ask your agency")}
        text={pending?.correction
          ? t("Nach der Freigabe kann nur deine Agentur die Rechnung ändern, zum Beispiel indem sie sie zur Korrektur zurückgibt. Schreib kurz, was falsch ist; die Nachricht geht mit Verweis auf die Rechnung in deinen Chat mit der Agentur.",
            "After approval only your agency can change the invoice, for example by returning it for correction. Briefly describe what is wrong; the message goes to your chat with the agency, linked to the invoice.")
          : archived
            ? t("Der Chat mit deiner früheren Agentur ist geschlossen. Die Nachfrage geht als Aufgabe mit Mail an ihr Team; die Antwort kommt per Mail an deine Anmeldeadresse.",
              "The chat with your former agency is closed. Your question goes to its team as a task with an email; the answer comes by email to your sign-in address.")
            : t("Die Nachricht geht mit Verweis auf die Rechnung in deinen Chat mit der Agentur.", "The message goes to your chat with the agency, linked to the invoice.")}
        confirm={t("Nachricht senden", "Send message")}
        busy={busy === "ask"}
        disabled={!message.trim()}
        onConfirm={() => void confirmPending()}
        onClose={() => setPending(null)}
      >
        <label>
          {t("Nachricht", "Message")}
          <textarea rows={4} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} />
        </label>
      </ConfirmDialog>
      <ConfirmDialog
        open={pending?.kind === "object"}
        title={t("Der Gutschrift widersprechen?", "Object to the self-billed invoice?")}
        text={t("Mit dem Widerspruch verliert die Gutschrift ihre Wirkung als deine Rechnung; es entsteht ein Stornobeleg mit eigener Nummer, den du per Mail bekommst. Deine Agentur sieht deinen Grund, stellt die Gutschrift richtig neu aus oder meldet sich bei dir. Eine Auszahlung dafür gibt es erst mit der neuen Gutschrift.",
          "With the objection the self-billed invoice no longer counts as your invoice; a cancellation document with its own number is created and emailed to you. Your agency sees your reason and reissues it correctly or gets in touch with you. The payout follows with the new self-billed invoice.")}
        confirm={t("Widersprechen", "Object")}
        danger
        busy={busy === "object"}
        disabled={message.trim().length < 5}
        onConfirm={() => void confirmPending()}
        onClose={() => setPending(null)}
      >
        <label>
          {t("Was stimmt nicht?", "What is wrong?")}
          <textarea rows={4} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder={t("z. B. Betrag, Leistungsmonat, Steuersatz oder deine Anschrift", "e.g. amount, service month, tax rate or your address")} />
        </label>
      </ConfirmDialog>
      <Dialog open={pending?.kind === "pay"} onClose={() => setPending(null)} title={t("Ist das Geld angekommen?", "Has the money arrived?")}>
        {pending?.kind === "pay" && (
          <>
            <p>
              {t("Rechnung", "Invoice")} {pending.v.row.number ?? t("ohne Nummer", "no number")} {t("über", "for")} <span className="num">{money(pending.v.row.gross_cents, pending.v.row.currency)}</span>.
              {pending.v.state.confirmationActive
                ? (pending.v.row.admin_payment_confirmed_at
                  ? t(` Deine Agentur sagt, sie hat am ${fmt(pending.v.row.admin_payment_confirmed_at)} ausgezahlt. Prüf dein Konto; mit deiner Meldung ist die Rechnung bezahlt.`, ` Your agency says it paid out on ${fmt(pending.v.row.admin_payment_confirmed_at)}. Check your account; with your report the invoice is paid.`)
                  : t(" Deine Agentur hat die Auszahlung schon bestätigt; mit deiner Meldung ist die Rechnung bezahlt.", " Your agency has already confirmed the payout; with your report the invoice is paid."))
                : t(" Deine Agentur bestätigt die Zahlung danach. Bis dahin kannst du die Meldung zurücknehmen.", " Your agency confirms the payment afterwards. Until then you can retract your report.")}
            </p>
            {/* Verständlichkeit E-04: die Frist vorher nennen, nicht erst hinterher im Verlauf */}
            {pending.v.state.confirmationActive && (b.payoutInfo?.get(pending.v.row.id)?.auto_confirm_at && new Date(b.payoutInfo.get(pending.v.row.id)!.auto_confirm_at!).getTime() > Date.now()
              ? <p><strong>{t(`Kommt bis zum ${fmt(b.payoutInfo.get(pending.v.row.id)!.auto_confirm_at)} keine Rückmeldung von dir, gilt das Geld als angekommen.`, `If you do not respond by ${fmt(b.payoutInfo.get(pending.v.row.id)!.auto_confirm_at)}, the money counts as received.`)}</strong></p>
              : null)}
            <div className="row">
              <button className="primary" disabled={busy === "pay"} onClick={() => void confirmPending("paid")}>{t("Ja, ist da", "Yes, it has arrived")}</button>
              {talentActions(pending.v, data.ctx).reportStillOverdue && (
                <button disabled={busy === "pay"} onClick={() => void confirmPending("still_overdue")}>{t("Nein, fehlt noch", "No, still missing")}</button>
              )}
            </div>
          </>
        )}
      </Dialog>
      <Dialog open={zip.open} onClose={() => setZip((z) => ({ ...z, open: false }))} title={t("Rechnungs-PDFs als ZIP", "Invoice PDFs as ZIP")}>
        <p>{t("Alle eingereichten Rechnungs-PDFs eines Jahres oder Monats in einer Datei, mit Gutschriften und Rechnungen deiner Agentur an dich, zum Beispiel für deine Steuererklärung.",
          "All submitted invoice PDFs of a year or month in one file, including self-billed invoices and invoices from your agency to you, for example for your tax return.")}</p>
        <div className="grid">
          <label>
            {t("Jahr", "Year")}
            <select value={zip.year} onChange={(e) => setZip((z) => ({ ...z, year: e.target.value }))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label>
            {t("Monat", "Month")}
            <select value={zip.month} onChange={(e) => setZip((z) => ({ ...z, month: e.target.value }))}>
              <option value="">{t("Ganzes Jahr", "Whole year")}</option>
              {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((m) => <option key={m} value={m}>{monthOf(`${zip.year}-${m}`, lang, false)}</option>)}
            </select>
          </label>
        </div>
        <p role="status">{zipRows.length === 1 ? t("1 Rechnung mit PDF", "1 invoice with PDF") : t(`${zipRows.length} Rechnungen mit PDF`, `${zipRows.length} invoices with PDF`)}</p>
        <div className="row">
          <button className="primary" onClick={() => void downloadZip()} disabled={!zipRows.length || busy === "zip"}>{busy === "zip" ? t("Wird zusammengestellt …", "Preparing …") : t("ZIP herunterladen", "Download ZIP")}</button>
          <button onClick={() => setZip((z) => ({ ...z, open: false }))}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </Dialog>
      <DemoNote />
    </main>
  );
}

function InvoicePanel({ r, ctx, archived, lang, timeZone, period, details, duplicates, stornoNumber, onAsk, payoutInfo, onPdf }: {
  payoutInfo?: CPayoutInfo | undefined;
  onPdf?: (() => void) | undefined;
  r: { v: TalentInvoiceView; brand: string; deal?: { title: string } | undefined };
  ctx: ReturnType<typeof talentStateContext>;
  archived: boolean;
  lang: Lang;
  timeZone: string;
  period: Parameters<typeof invoiceTimeline>[1];
  details: { lines: { id: string; invoice_id: string; position: number; description: string; quantity: number; unit_cents: number; net_cents: number; commission_free: boolean }[]; attachments: CAttachment[]; events: CInvoiceEvent[] } | undefined;
  duplicates: string[];
  /** K-448: Nummer des Stornobelegs zu dieser Gutschrift */
  stornoNumber?: string | null;
  onAsk: (kind: AskKind) => void;
}) {
  const t = useT();
  const { scope } = useCreatorContext();
  const late = useCreatorAction();
  const fmt = (d: string | null | undefined) => dateOf(d ?? null, lang, timeZone);
  const v = r.v;
  const row = v.row;
  const acts = talentActions(v, ctx);
  const lines = (details?.lines ?? []).filter((l) => l.invoice_id === row.id);
  const files = (details?.attachments ?? []).filter((a) => a.owner_id === row.id);
  // K-147 c: Freigabe trotz Prüfhinweisen einmal (nicht zusätzlich „freigegeben“); K-160: interne Schritte der Agentur (Weiterleitung
  // an die Buchhaltung, Ablage) sieht der Creator nicht
  const intern = new Set(["accounting_forwarded", "pdf_archived", "mail_failed"]);
  const alleEvents = (details?.events ?? []).filter((e) => e.invoice_id === row.id && !intern.has(e.event));
  const mitHinweisen = new Set(alleEvents.filter((e) => e.event === "approved_with_findings").map((e) => String(e.at).slice(0, 16)));
  const events = alleEvents.filter((e) => !(e.event === "approved" && mitHinweisen.has(String(e.at).slice(0, 16))));
  // Nach dem Storno ist „Zahlung zurückgesetzt“ der Vermerk der Rückzahlung (Nachtest K-157 im Creator-Portal)
  const storniertAm = alleEvents.find((e) => e.event === "cancelled")?.at ?? null;
  const textFuer = (e: { event: string; at: unknown }) => e.event === "payment_reset" && storniertAm && String(e.at) >= String(storniertAm)
    ? tr(lang, "Deine Rückzahlung wurde vermerkt", "Your repayment has been noted")
    // K-448: der Beleg zur Gutschrift heißt Stornobeleg
    : e.event === "created_credit_note" && row.self_billed ? tr(lang, "Stornobeleg erstellt", "Cancellation document created")
    : eventText(e.event, lang);
  const statusText = row.self_billed && row.credit_for
    ? t(`Brutto · Stornobeleg vom ${fmt(row.issue_date ?? row.created_at)}`, `Gross · cancellation document of ${fmt(row.issue_date ?? row.created_at)}`)
    : row.self_billed
    ? t(`Brutto · Gutschrift vom ${fmt(row.issue_date ?? row.created_at)}`, `Gross · self-billed on ${fmt(row.issue_date ?? row.created_at)}`)
    : row.submitted_at
    ? t(`Brutto · eingereicht am ${fmt(row.submitted_at)}`, `Gross · submitted on ${fmt(row.submitted_at)}`)
    : row.status === "draft" ? t(`Brutto · Entwurf vom ${fmt(row.created_at)}`, `Gross · draft from ${fmt(row.created_at)}`) : t("Brutto", "Gross");
  const needsFix = v.state.state === "correction_needed";
  const work = !archived;
  // „Wann kommt mein Geld?“ (Befund Creator 13): voraussichtliche Auszahlung aus Modus und Zahlung der Marke
  const expected = payoutExpectation(row, payoutInfo, lang, fmt);
  const canAskCorrection = work && !row.self_billed && ["approved", "sent"].includes(row.status) && !row.paid_at;
  return (
    <section className="panel stack tk-cr-beleg" aria-labelledby={`rechnung-${row.id}`}>
      <h3 id={`rechnung-${row.id}`}>{row.number ?? t("Entwurf ohne Nummer", "Draft without number")}{r.brand ? ` · ${r.brand}` : ""}</h3>
      <PillView pill={v.pill} />
      <div className="metric num">{money(row.gross_cents, row.currency)}</div>
      <p>{statusText}{row.revenue_month ? t(` · Leistungsmonat ${monthOf(row.revenue_month, lang)}`, ` · service month ${monthOf(row.revenue_month, lang)}`) : ""}</p>
      {/* Im Archiv ist der Monat nicht mehr lesbar: ohne Stand der Marke statt „ausstehend“ */}
      <Timeline steps={invoiceTimeline(v, period, (d) => fmt(d), lang, payoutInfo?.brand_state ?? null).filter((s) => !(archived && s.label === t("Zahlung der Marke", "Payment by the brand")))} />
      {(needsFix || (row.status === "draft" && row.reviewed_at && row.revision_reason)) && (
        <div className="revision-note">{needsFix ? "" : t("Korrektur in Arbeit · ", "Correction in progress · ")}{t("Hinweis deiner Agentur", "Note from your agency")}: {row.revision_reason?.trim() || t("Bitte korrigiere die Rechnung.", "Please correct the invoice.")}</div>
      )}
      {/* 0345: Stornobeleg zur Gutschrift */}
      {row.self_billed && row.credit_for && (
        <div className="notice">
          {t("Stornobeleg: Deine Agentur hat die Gutschrift mit diesem Beleg storniert. Er hat eine eigene Nummer und negative Beträge; bewahre ihn zusammen mit der Gutschrift auf. Keine Auszahlung: Ein bereits ausgezahlter Betrag wird zurückgefordert oder verrechnet.",
            "Cancellation document: your agency has cancelled the self-billed invoice with this document. It has its own number and negative amounts; keep it together with the self-billed invoice. No payout: any amount already paid out will be reclaimed or offset.")}
        </div>
      )}
      {/* K-448: an der stornierten Gutschrift der Verweis auf den Stornobeleg, kein Widerspruchssatz mehr */}
      {row.self_billed && !row.credit_for && row.status === "cancelled" && stornoNumber && (
        <div className="notice">{t(`Storniert mit Stornobeleg ${stornoNumber}.`, `Cancelled with cancellation document ${stornoNumber}.`)}</div>
      )}
      {row.self_billed && !row.credit_for && !row.objected_at && row.status !== "cancelled" && (
        <div className="notice">
          {t("Gutschrift: Deine Agentur hat diese Rechnung nach eurer Vereinbarung in deinem Namen ausgestellt. Prüfe Betrag, Leistungsmonat und deine Angaben. Stimmt etwas nicht, widersprich; dann verliert sie ihre Wirkung und deine Agentur stellt sie neu aus.",
            "Self-billed invoice: your agency issued this invoice in your name as agreed. Check the amount, service month and your details. If something is wrong, object; it then no longer counts and your agency reissues it.")}
        </div>
      )}
      {row.self_billed && row.objected_at
        ? <div className="notice">{t(`Du hast am ${fmt(row.objected_at)} widersprochen`, `You objected on ${fmt(row.objected_at)}`)}{row.objection_reason ? `: ${row.objection_reason}` : "."}</div>
        : row.status === "cancelled" && row.cancel_reason && <div className="notice">{t("Storniert", "Cancelled")}: {row.cancel_reason}</div>}
      {duplicates.length > 0 && <div className="notice">{t(`Für denselben Monat gibt es noch ${duplicates.length === 1 ? "die Rechnung" : "die Rechnungen"} ${duplicates.join(", ")}.`, `There ${duplicates.length === 1 ? "is another invoice" : "are other invoices"} for the same month: ${duplicates.join(", ")}.`)}</div>}
      {v.state.openPaymentCheck && acts.reportPaid && <div className="notice">{t("Deine Agentur fragt: Ist das Geld für diese Rechnung angekommen?", "Your agency asks: has the money for this invoice arrived?")}</div>}
      {expected && v.state.state !== "payout_overdue" && <p><strong>{expected}</strong></p>}
      {v.state.state === "payout_overdue" && (
        <div className="notice">{t("Die Auszahlung an dich ist überfällig. Frag bei deiner Agentur nach.", "The payout to you is overdue. Ask your agency.")}</div>
      )}
      {v.state.state === "send_overdue" && (
        <div className="notice">
          {row.kind === "talent_to_agency"
            ? t("Die Auszahlung an dich ist überfällig. Frag bei deiner Agentur nach; der Verlauf zeigt, wann sie freigegeben hat.", "The payout to you is overdue. Ask your agency; the history shows when it was approved.")
            : t("Deine Agentur hat die Rechnung freigegeben, aber noch nicht an die Marke gesendet. Frag bei ihr nach.", "Your agency approved the invoice but has not sent it to the brand yet. Ask your agency.")}
        </div>
      )}
      {row.pdf_origin === "uploaded" && row.pdf_confirmed_at && <small>{t(`Eigene Datei · Nummer und Betrag von dir bestätigt am ${fmt(row.pdf_confirmed_at)}`, `Own file · number and amount confirmed by you on ${fmt(row.pdf_confirmed_at)}`)}</small>}
      {row.pdf_origin === "generated" && <small>{t("Rechnung aus deinen Angaben im Portal erzeugt", "Invoice created from your details in the portal")}</small>}

      {lines.length > 0 && (
        <div className="tablewrap tk-cardtable tk-cardwide">
          <table>
            <caption className="sr-only">{t("Positionen", "Items")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Position", "Item")}</th>
                <th scope="col">{t("Menge", "Qty")}</th>
                <th scope="col" className="money">{t("Einzelpreis", "Unit price")}</th>
                <th scope="col" className="money">{t("Summe netto", "Net total")}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td data-label={t("Position", "Item")}>{l.description}{l.commission_free && <small>{t("provisionsfrei", "commission-free")}</small>}</td>
                  <td data-label={t("Menge", "Qty")}>{lang === "en" ? String(l.quantity) : String(l.quantity).replace(".", ",")}</td>
                  <td data-label={t("Einzelpreis", "Unit price")}><span className="num">{money(l.unit_cents, row.currency)}</span></td>
                  <td data-label={t("Summe netto", "Net total")}><span className="num">{money(l.net_cents, row.currency)}</span></td>
                </tr>
              ))}
              <tr>
                <td colSpan={3} className="tk-cardhide">{t("Netto · Umsatzsteuer", "Net · VAT")}</td>
                <td data-label={t("Netto · Umsatzsteuer", "Net · VAT")}><span className="num">{money(row.net_cents, row.currency)}</span><small className="num">+ {money(row.tax_cents, row.currency)}</small></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {files.length > 0 && (
        <div className="stack tk-gap-sm">
          <strong>{t("Belege", "Documents")}</strong>
          {files.map((f) => (
            <div key={f.id} className="row between">
              <span>{f.file_name} <small>{fmt(f.created_at)}{f.storage_path === row.pdf_path ? t(" · Rechnung", " · invoice") : f.e_invoice?.origin === "generated" ? t(" · XRechnung zur Rechnung", " · XRechnung of the invoice") : t(" · weiterer Beleg", " · other document")}
                {f.e_invoice ? ` · ${t("E-Rechnung", "e-invoice")} ${eInvoiceFormatLabel(f.e_invoice, lang)}` : ""}</small></span>
              <div className="row">
                <button onClick={() => void openDocument(f.storage_path, f.file_name, f.e_invoice && f.mime_type !== "application/pdf" ? { sha256: f.sha256, erwartet: f.category === "invoice" ? expectedOfInvoice(row, null, null) : null } : undefined)}>{t("Öffnen", "Open")}</button>
                {f.e_invoice && f.mime_type === "application/pdf" && (
                  <button onClick={() => void openDocument(f.storage_path, f.file_name, { sha256: f.sha256, erwartet: f.category === "invoice" ? expectedOfInvoice(row, null, null) : null })}>{t("E-Rechnung ansehen", "View e-invoice")}</button>
                )}
                <button onClick={() => void downloadDocument(f.storage_path, f.file_name)}>{t("Herunterladen", "Download")}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!archived && canAddLateReceipt(row) && (
        // Beleg nachreichen (PARITAET-4): die Rechnung selbst bleibt unverändert; deine Agentur bekommt eine Meldung
        <label>
          {t("Beleg nachreichen (PDF, JPG, PNG oder E-Rechnung als XML bzw. ZUGFeRD, höchstens 15 MB)", "Add a receipt later (PDF, JPG, PNG or e-invoice as XML or ZUGFeRD, at most 15 MB)")}
          <input type="file" accept={BELEG_ACCEPT} disabled={!!late.busy} onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void late.run("beleg", () => addLateReceipt(api, scope, row.id, file, file.name, lang), t(`${file.name} nachgereicht. Deine Agentur sieht den Beleg jetzt.`, `${file.name} added. Your agency can see the receipt now.`));
          }} />
          <small>{t("Zum Beispiel eine Hotelrechnung, die später kam. Deine Rechnung ändert sich dadurch nicht.", "For example a hotel bill that arrived later. Your invoice does not change.")}</small>
        </label>
      )}

      {events.length > 0 && (
        <div className="stack tk-gap-sm">
          <strong>{t("Verlauf", "History")}</strong>
          <ul>
            {events.map((e) => (
              <li key={e.id}>
                {fmt(e.at)} · {textFuer(e)}
                {e.event === "comment" && typeof e.details?.text === "string" ? `: ${e.details.text}` : ""}
                {(e.event === "revision_requested" || e.event === "rejected") && typeof e.details?.note === "string" && e.details.note ? `: ${e.details.note}` : ""}
                {e.event === "offset" && typeof e.details?.with_number === "string" ? ` (${e.details.with_number})` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="row">
        {onPdf ? <button onClick={onPdf}>{t("PDF öffnen", "Open PDF")}</button> : acts.openPdf && <button onClick={() => void openDocument(row.pdf_path!)}>{t("PDF öffnen", "Open PDF")}</button>}
        {canAskCorrection && <button onClick={() => onAsk("correction")}>{t("Korrektur anfragen", "Request a correction")}</button>}
        {work && acts.submit && <Link className="btn primary" to={`/creator/rechnungen/einreichen?rechnung=${row.id}&schritt=4`}>{t("Rechnung einreichen", "Submit invoice")}</Link>}
        {work && acts.edit && <Link className="btn" to={`/creator/rechnungen/einreichen?rechnung=${row.id}`}>{needsFix ? t("Rechnung korrigieren", "Correct invoice") : t("Weiter bearbeiten", "Continue editing")}</Link>}
        {acts.reportPaid && <button className="primary" onClick={() => onAsk("pay")}>{t("Zahlung melden", "Report payment")}</button>}
        {acts.retractPayment && <button onClick={() => onAsk("retract")}>{t("Meldung zurücknehmen", "Retract report")}</button>}
        {acts.askAgency && (work || (row.kind === "talent_to_agency" && ["approved", "sent"].includes(row.status))) && <button onClick={() => onAsk("ask")}>{t("Bei der Agentur nachfragen", "Ask your agency")}</button>}
        {work && acts.withdrawSubmission && <button onClick={() => onAsk("withdrawSubmission")}>{t("Einreichung zurückziehen", "Withdraw submission")}</button>}
        {work && acts.acceptSelfBilling && <button className="primary" onClick={() => onAsk("accept")}>{t("Passt so", "Looks right")}</button>}
        {work && acts.objectSelfBilling && <button className="danger" onClick={() => onAsk("object")}>{t("Widersprechen", "Object")}</button>}
        {work && acts.withdraw && <button className="danger" onClick={() => onAsk("withdraw")}>{t("Rechnung zurückziehen", "Withdraw invoice")}</button>}
        {work && acts.discard && row.status === "draft" && <button className="danger" onClick={() => onAsk("discard")}>{t("Entwurf verwerfen", "Discard draft")}</button>}
      </div>
      {work && acts.withdrawSubmissionTooLate && (
        <p>
          {t("Deine Agentur hat die Rechnung schon geprüft; zurückziehen geht nicht mehr.", "Your agency has already reviewed the invoice; it can no longer be withdrawn.")} <Link to="/creator/nachrichten">{t("Schreib ihr", "Write to it")}</Link>{t(", wenn du etwas ändern musst.", " if you need to change something.")}
        </p>
      )}
    </section>
  );
}

/** Rechnung der Agentur an den Creator: offen, versendet, noch keine Meldung (CREATOR-R3-6). */
export function canReportTransfer(i: Pick<CInvoice, "kind" | "status" | "paid_at" | "talent_payment_response" | "in_collection">): boolean {
  return i.kind === "agency_to_talent" && i.status === "sent" && !i.paid_at && !i.in_collection && i.talent_payment_response !== "paid";
}

/**
 * „Wann kommt mein Geld?“ (Befund Creator 13): voraussichtliche Auszahlung aus Modus und Zahlung der Marke. Nach der eigenen
 * Eingangsmeldung (CREATOR-7) steht dort die Meldung statt eines oft schon vergangenen „voraussichtlich bis“.
 */
export function payoutExpectation(row: Pick<CInvoice, "kind" | "status" | "paid_at" | "admin_payment_confirmed_at" | "talent_payment_response" | "talent_payment_response_at"
  | "effective_due_date" | "document_due_date" | "issuer_due_date"> & Partial<Pick<CInvoice, "gross_cents" | "paid_cents" | "currency">>, payoutInfo: Pick<CPayoutInfo, "mode" | "payout_due"> | undefined, lang: Lang, fmt: (d: string | null | undefined) => string): string | null {
  const t = (de: string, en: string) => tr(lang, de, en);
  // K-448: ein Stornobeleg wird nicht ausgezahlt
  const unpaidPayout = row.kind === "talent_to_agency" && !(row as { credit_for?: string | null }).credit_for && ["approved", "sent"].includes(row.status) && !row.paid_at && !row.admin_payment_confirmed_at;
  if (!unpaidPayout) return null;
  // Anteilig ausgezahlt (GELD-R3-3): der Rest folgt, nicht „noch offen“ über den ganzen Betrag
  const part = partialPaidOutCents({ kind: row.kind, status: row.status, paid_at: row.paid_at, admin_payment_confirmed_at: row.admin_payment_confirmed_at,
    gross_cents: row.gross_cents ?? 0, paid_cents: row.paid_cents ?? 0 });
  if (part > 0) {
    const rest = money((row.gross_cents ?? 0) - part, row.currency ?? "EUR");
    return payoutInfo?.mode === "after_brand_payment" && !payoutInfo.payout_due
      ? t(`Anteilig ausgezahlt: ${money(part, row.currency ?? "EUR")}. Der Rest (${rest}) folgt, sobald die Marke weiter gezahlt hat.`, `Partly paid out: ${money(part, row.currency ?? "EUR")}. The rest (${rest}) follows once the brand has paid more.`)
      : t(`Anteilig ausgezahlt: ${money(part, row.currency ?? "EUR")}. Der Rest (${rest}) kommt mit der nächsten Auszahlung.`, `Partly paid out: ${money(part, row.currency ?? "EUR")}. The rest (${rest}) comes with the next payout.`);
  }
  if (row.talent_payment_response === "paid") {
    return t(`Du hast den Eingang am ${fmt(row.talent_payment_response_at)} gemeldet; deine Agentur bestätigt ihn.`, `You reported receipt on ${fmt(row.talent_payment_response_at)}; your agency will confirm it.`);
  }
  if (payoutInfo?.mode === "after_brand_payment" && !payoutInfo.payout_due) {
    return t("Auszahlung nach Zahlung der Marke (noch offen); danach innerhalb der vereinbarten Frist.", "Payout after the brand pays (still open); then within the agreed period.");
  }
  const due = payoutInfo?.payout_due ?? row.effective_due_date ?? row.document_due_date ?? row.issuer_due_date;
  return due ? t(`Auszahlung voraussichtlich bis ${fmt(due)}.`, `Payout expected by ${fmt(due)}.`) : null;
}
